import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Button } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flex } from '../../../base/components/Flex';
import HasPermission from '../../../base/components/UserProvider/HasPermission';
import { useUser } from '../../../base/components/UserProvider/useUserHook';
import {
  useBoxDevices,
  VrDeviceType,
} from '../../../base/components/VrDevicesProvider/VrDevicesProvider';
import {
  DeviceState,
  UserPermission,
} from '../../../graphql/graphql-operations';
import { useTranslationPrefix } from '../../../hooks/useTranslationPrefix';
import ContentWrapper from '../../components/ContentWrapper';
import PageHeader from '../../components/PageHeader';
import EditDeviceDialog from './components/EditDeviceDialog';
import RegisterDeviceDialog from './components/RegisterDeviceDialog';
import { DeviceList } from './DeviceList';
import DeviceUpdateDialog from './DeviceUpdateDialog';
import useDeviceRestart from './useDeviceRestart';
import useDeviceUpdate from './useDeviceUpdate';

const CompanyDevicesPage = () => {
  const navigate = useNavigate();
  const { restartVrDevice } = useDeviceRestart();
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [deviceToEdit, setDeviceToEdit] = useState<VrDeviceType | null>(null);
  const { checkDeviceUpdatable } = useDeviceUpdate();
  const boxDevices = useBoxDevices();
  const { hasPermission } = useUser();

  const firstActiveSessionDevice = useMemo(
    () =>
      boxDevices.find(
        (d) => !!d.session?.id && d.status.state !== DeviceState.OFFLINE,
      ),
    [boxDevices],
  );

  const [deviceToUpdate, setDeviceToUpdate] = useState<VrDeviceType | null>(
    null,
  );
  const { _t } = useTranslationPrefix('companyDevicePage');

  const goToSession = useCallback((device: VrDeviceType) => {
    const sessionId = device.session?.id;
    if (sessionId) {
      navigate(`/sessions/${sessionId}`);
    }
  }, []);

  const handleOpenAllClick = useCallback(() => {
    const sessionId = firstActiveSessionDevice?.session?.id;
    if (sessionId) {
      navigate(`/sessions/${sessionId}?multiple`);
    }
  }, []);

  const handleDeviceUpdateClick = useCallback(
    (device: VrDeviceType) => {
      const updatable = checkDeviceUpdatable(device);
      if (updatable) {
        setDeviceToUpdate(device);
      }
    },
    [checkDeviceUpdatable],
  );

  return (
    <ContentWrapper>
      <PageHeader back title={_t('companyDevices', 'Company devices')}>
        <HasPermission val={UserPermission.MANAGE_COMPANY_VR_DEVICES}>
          <Button
            id="register-new"
            onClick={() => setRegisterDialogOpen(true)}
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            color="bgContrast"
          >
            {_t('newDevice', 'Add device')}
          </Button>
        </HasPermission>
        <Button
          id="openAll"
          disabled={!firstActiveSessionDevice}
          onClick={handleOpenAllClick}
          variant="contained"
        >
          {_t('openAll', 'Open all')}
        </Button>
      </PageHeader>
      <Flex justifyContent="center">
        <DeviceList
          selectedDevices={[]}
          onDeviceUpdateClick={handleDeviceUpdateClick}
          isActive={(device: VrDeviceType) => !!device.session?.id}
          onDeviceClick={goToSession}
          onDeviceEditClick={
            hasPermission(UserPermission.UPDATE_COMPANY_VR_DEVICES)
              ? setDeviceToEdit
              : undefined
          }
          onDeviceRestartClick={restartVrDevice}
        />
      </Flex>
      <DeviceUpdateDialog
        device={deviceToUpdate}
        onClose={() => setDeviceToUpdate(null)}
      ></DeviceUpdateDialog>
      <RegisterDeviceDialog
        open={registerDialogOpen}
        onClose={() => setRegisterDialogOpen(false)}
      />
      <EditDeviceDialog
        vrDeviceId={deviceToEdit?.id || null}
        onClose={() => setDeviceToEdit(null)}
      />
    </ContentWrapper>
  );
};

export default CompanyDevicesPage;
